/**
 * The 24x24 stroke icon set the dashboard draws from. Sized by the caller's
 * font-size, so a KPI tile and a chip can share one glyph.
 *
 * Extracted from JobsListScreen so the reports queue renders the same marks —
 * two icon sets would drift apart the first time either screen gained a state.
 */

export type IconName =
  | 'clipboard'
  | 'send'
  | 'clock'
  | 'alert'
  | 'alert-circle'
  | 'calendar'
  | 'check'
  | 'refresh'
  | 'chevron'
  | 'caret'
  | 'flag'
  | 'users'
  | 'up'
  | 'down'
  | 'plus'
  | 'minus'
  | 'dollar'
  | 'expand';

export function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, JSX.Element> = {
    clipboard: (
      <>
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 4h6v3H9z" />
        <path d="M9 12h6M9 16h4" />
      </>
    ),
    send: <path d="M21 3 3 10.5l7 3 3 7L21 3z" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 2" />
      </>
    ),
    alert: (
      <>
        <path d="M12 4.5 21 19.5H3L12 4.5z" />
        <path d="M12 10v4M12 16.6v.2" />
      </>
    ),
    'alert-circle': (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 8v4.5M12 15.6v.2" />
      </>
    ),
    calendar: (
      <>
        <rect x="3.5" y="5" width="17" height="15" rx="2" />
        <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="m8.5 12.2 2.5 2.5 4.5-5" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 12a8 8 0 1 1-2.6-5.9" />
        <path d="M20 4v4.5h-4.5" />
      </>
    ),
    chevron: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
    caret: <path d="m6 9.5 6 6 6-6" />,
    flag: (
      <>
        <path d="M6 21V4" />
        <path d="M6 5h11l-2.5 4L17 13H6z" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3.4" />
        <path d="M3.5 20c0-3.3 2.5-5.4 5.5-5.4s5.5 2.1 5.5 5.4" />
        <path d="M16 5.4a3.4 3.4 0 0 1 0 6.5M17.5 14.9c1.9.6 3.2 2.4 3.2 5.1" />
      </>
    ),
    up: <path d="m12 6 6 9H6l6-9z" />,
    down: <path d="m12 18 6-9H6l6 9z" />,
    plus: <path d="M12 6v12M6 12h12" />,
    minus: <path d="M6 12h12" />,
    dollar: (
      <>
        <path d="M12 4v16" />
        <path d="M15.5 8.2A3.2 3.2 0 0 0 12.4 6h-1a2.9 2.9 0 0 0 0 5.8h1.2a2.9 2.9 0 0 1 0 5.8h-1.1a3.2 3.2 0 0 1-3.1-2.2" />
      </>
    ),
    expand: <path d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5" />,
  };
  const filled = name === 'up' || name === 'down' || name === 'send';
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}
